import { useCallback, useEffect, useRef, useState } from 'react';
import { AxiosService } from 'src/axios/axios.service';
import { useSocketsContext } from 'src/providers/SocketsProvider/Sockets.context';
import {
  billingPauseReason,
  isBillingGateName,
} from 'src/components/Chat/Chat.functions';

// Workspace "is the Lead Agent paused by billing?" signal from leadbilling's
// token-availability: any `allowed:false` is a pause, with its `reason`. Fail-open:
// loading / error / allowed:true → not paused (never flash a false pause).
//
// A `smartleadbot.session.gate_failed` with a `billing_*` gate_name for this
// workspace marks it paused at once; a fetch started after it that answers
// allowed:true clears it. A fetch already in flight when the live pause arrived
// describes an older state, so its allowed:true answer or its error is ignored
// (its allowed:false still applies).
//
// It makes an HTTP request: call it once (`useChat` does) and read the chat context.

type tTokenAvailabilityEnvelope = {
  data?: { allowed?: boolean; reason?: string };
};

export type tLeadAgentPaused = { paused: boolean; reason: string | null };

/** The workspace's paused state, plus `refetch` to ask token-availability again. */
export type tUseLeadAgentPaused = tLeadAgentPaused & {
  refetch: () => void;
};

const NOT_PAUSED: tLeadAgentPaused = { paused: false, reason: null };

/**
 * Whether billing paused the workspace's Lead Agent, from token-availability
 * and from the live `billing_*` gate_failed events.
 * @param {string | undefined} workspaceId - The current workspace; no request
 *   is made without one.
 * @returns {tUseLeadAgentPaused} The paused state, its reason and `refetch`.
 */
export function useLeadAgentPaused(workspaceId: string | undefined): tUseLeadAgentPaused {
  const [state, setState] = useState<tLeadAgentPaused>(NOT_PAUSED);
  const [refetchCount, setRefetchCount] = useState(0);
  const enabled = !!workspaceId;
  const { slbSessionGateFailedEvent } = useSocketsContext();
  const livePauseCount = useRef(0);

  const refetch = useCallback(() => setRefetchCount((count) => count + 1), []);

  // Another workspace's state never carries over, not even until its fetch answers.
  useEffect(() => {
    setState(NOT_PAUSED);
  }, [workspaceId]);

  slbSessionGateFailedEvent.useSubscription((event) => {
    if (!enabled || event.workspace_id !== workspaceId) return;
    if (!isBillingGateName(event.gate_name)) return;
    livePauseCount.current += 1;
    setState({ paused: true, reason: billingPauseReason(event.gate_name) });
  });

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    let pending: AbortController | null = null;

    const fetchAvailability = () => {
      pending?.abort();
      pending = new AbortController();
      const livePausesAtStart = livePauseCount.current;
      AxiosService.get<tTokenAvailabilityEnvelope>(
        `workspaces/${workspaceId}/lead-agent/token-availability`,
        { isBillingService: true, signal: pending.signal, headers: { 'x-workspace-id': workspaceId } },
      )
        .then((res) => {
          if (!active) return;
          const allowed = res?.data?.allowed;
          if (allowed !== false && livePauseCount.current !== livePausesAtStart) return;
          const reason = res?.data?.reason ?? null;
          setState(allowed === false ? { paused: true, reason } : NOT_PAUSED);
        })
        .catch((e: unknown) => {
          const name = (e as { name?: string })?.name;
          // Ignore aborts (superseded fetch / cleanup); any real error fails open,
          // unless a live pause arrived while this fetch was in flight.
          if (name === 'CanceledError' || name === 'AbortError' || !active) return;
          if (livePauseCount.current !== livePausesAtStart) return;
          setState(NOT_PAUSED);
        });
    };

    fetchAvailability();
    const onFocus = () => fetchAvailability();
    window.addEventListener('focus', onFocus);

    return () => {
      active = false;
      pending?.abort();
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, workspaceId, refetchCount]);

  return { ...state, refetch };
}
